/**
 * GroupRequestApprovalActions Component
 *
 * Action buttons for approving or rejecting group requests.
 * Pattern based on FAQs ApprovalActionsButtons.tsx
 *
 * Features:
 * - Approve button (green/success color)
 * - Reject button (red/error color)
 * - Opens rejection dialog when reject is clicked
 * - Loading states during API calls
 * - Success/error messages via snackbar context
 * - Automatic query invalidation after actions
 *
 * Responsibilities:
 * - Renders approve/reject buttons
 * - Manages rejection dialog state
 * - Calls mutation hooks
 * - Handles success/error callbacks
 *
 * Usage:
 * ```tsx
 * <GroupRequestApprovalActions
 *   request={groupRequest}
 *   onSuccess={() => showSuccess('Request processed')}
 *   onError={(msg) => showError(msg)}
 * />
 * ```
 */

import React, { useState } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { CheckCircle as ApproveIcon, Cancel as RejectIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { GroupRequest } from '../../api/data-contracts';
import { useApproveGroupRequest, useRejectGroupRequest } from '../../hooks/api/useGroupRequests';
import { GroupRequestRejectionDialog } from './GroupRequestRejectionDialog';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { log } from '../../utils/errors/logger';

interface GroupRequestApprovalActionsProps {
  /**
   * Group request to approve/reject
   */
  request: GroupRequest;
  /**
   * Button size
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Optional success callback
   */
  onSuccess?: () => void;
  /**
   * Optional error callback
   */
  onError?: (message: string) => void;
}

export const GroupRequestApprovalActions: React.FC<GroupRequestApprovalActionsProps> = ({
  request,
  size = 'small',
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  // Mutation hooks with success/error handling
  const approveMutation = useApproveGroupRequest();
  const rejectMutation = useRejectGroupRequest();

  const handleApprove = async () => {
    if (!request.id) {
      const errorMsg = t('groupRequests.approval.errors.invalidRequest');
      showError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    try {
      await approveMutation.mutateAsync({
        requestId: request.id,
        approval: {
          admin_notes: null,
        },
      });

      const successMsg = t('groupRequests.approval.messages.approveSuccess');
      showSuccess(successMsg);
      onSuccess?.();

      log.user.action('group_request_approved', 'GroupRequestApprovalActions', {
        requestId: request.id,
        groupName: request.group_name,
      });
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : t('groupRequests.approval.errors.approveFailed');
      showError(errorMsg);
      onError?.(errorMsg);

      log.error('[GroupRequestApprovalActions] Failed to approve request', {
        requestId: request.id,
        error,
      });
    }
  };

  const handleReject = async (reason: string) => {
    if (!request.id) {
      const errorMsg = t('groupRequests.approval.errors.invalidRequest');
      showError(errorMsg);
      onError?.(errorMsg);
      setRejectDialogOpen(false);
      return;
    }

    try {
      await rejectMutation.mutateAsync({
        requestId: request.id,
        rejection: {
          rejection_reason: reason,
          admin_notes: null,
        },
      });

      const successMsg = t('groupRequests.approval.messages.rejectSuccess');
      showSuccess(successMsg);
      onSuccess?.();
      setRejectDialogOpen(false);

      log.user.action('group_request_rejected', 'GroupRequestApprovalActions', {
        requestId: request.id,
        groupName: request.group_name,
        reason,
      });
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : t('groupRequests.approval.errors.rejectFailed');
      showError(errorMsg);
      onError?.(errorMsg);

      log.error('[GroupRequestApprovalActions] Failed to reject request', {
        requestId: request.id,
        error,
      });
    }
  };

  const isLoading = approveMutation.isPending || rejectMutation.isPending;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Approve Button */}
        <Tooltip title={t('groupRequests.approval.actions.approveTooltip')}>
          <Button
            size={size}
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            onClick={handleApprove}
            disabled={isLoading}
          >
            {approveMutation.isPending
              ? t('common.submitting')
              : t('groupRequests.approval.actions.approve')}
          </Button>
        </Tooltip>

        {/* Reject Button */}
        <Tooltip title={t('groupRequests.approval.actions.rejectTooltip')}>
          <Button
            size={size}
            variant="outlined"
            color="error"
            startIcon={<RejectIcon />}
            onClick={() => setRejectDialogOpen(true)}
            disabled={isLoading}
          >
            {t('groupRequests.approval.actions.reject')}
          </Button>
        </Tooltip>
      </Box>

      {/* Rejection Dialog */}
      <GroupRequestRejectionDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleReject}
        requestId={request.id}
        isSubmitting={rejectMutation.isPending}
      />
    </>
  );
};
